import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FileText, Clock, Paperclip, Link2, Download,
  AlertTriangle, ChevronDown, ChevronRight, Users, Eye, Edit3,
  CheckCircle2, X, Upload, Shield, MessageSquare, Save, Image as ImageIcon,
  BookOpen, Plus,
} from 'lucide-react';
import {
  API, ISSUE_TYPES, STATUS_OPTIONS, headers, getToken, StatusBadge, IssueLabel,
} from './shared';
import {
  GrievantFactSheetPanel,
  StewardFactSheetPanel,
  SpecTimelinePanel,
  Step2ChecklistPanel,
} from './factSheets';

function CaseDetail({ caseId, onBack }) {
  const [c, setCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editPerson, setEditPerson] = useState({ name: '', role: '' });
  const [editWitness, setEditWitness] = useState('');

  const fileInputRef = useRef(null);

  const loadCase = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/case-locker/cases/${caseId}`, { headers: headers(false) });
      const data = await res.json();
      if (data.ok) setCase(data.case);
    } catch (e) { console.warn('load case failed', e); }
    setLoading(false);
  }, [caseId]);

  useEffect(() => { loadCase(); }, [loadCase]);

  function enterEditMode() {
    if (!c) return;
    setEditForm({
      title: c.title || '',
      issue_type: c.issue_type || 'other',
      incident_date: c.incident_date || '',
      summary: c.summary || '',
      notes: c.notes || '',
      people_involved: [...(c.people_involved || [])],
      witnesses: [...(c.witnesses || [])],
    });
    setEditPerson({ name: '', role: '' });
    setEditWitness('');
    setEditMode(true);
  }

  function cancelEdit() {
    setEditMode(false);
    setEditForm(null);
  }

  function addEditPerson() {
    if (!editPerson.name.trim()) return;
    setEditForm(f => ({
      ...f,
      people_involved: [...f.people_involved, {
        name: editPerson.name.trim(),
        role: editPerson.role.trim(),
        notes: '',
      }],
    }));
    setEditPerson({ name: '', role: '' });
  }

  function addEditWitness() {
    if (!editWitness.trim()) return;
    setEditForm(f => ({ ...f, witnesses: [...f.witnesses, editWitness.trim()] }));
    setEditWitness('');
  }

  async function saveEdit() {
    if (!editForm || !editForm.title.trim()) return;
    setEditSaving(true);
    try {
      const res = await fetch(`${API}/api/case-locker/cases/${caseId}`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (data.ok) {
        setCase(data.case);
        setEditMode(false);
        setEditForm(null);
      }
    } catch (e) { console.warn('save edit failed', e); }
    setEditSaving(false);
  }

  async function addNote() {
    if (!noteText.trim()) return;
    await fetch(`${API}/api/case-locker/cases/${caseId}/timeline`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ action_type: 'note_added', description: noteText.trim() })
    });
    setNoteText('');
    loadCase();
  }

  async function changeStatus(newStatus) {
    await fetch(`${API}/api/case-locker/cases/${caseId}/status`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ status: newStatus })
    });
    setShowStatusMenu(false);
    loadCase();
  }

  const MAX_UPLOAD_BYTES = 20 * 1024 * 1024; // keep in sync with backend
  const ALLOWED_UPLOAD_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

  async function uploadFilesList(files) {
    if (!files || !files.length) return;
    setUploadError('');
    const file = files[0]; // Phase 2: single file per interaction
    if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
      setUploadError(`Unsupported file type: ${file.type || 'unknown'}. Only JPG, PNG, PDF.`);
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(`File too large: ${(file.size / (1024 * 1024)).toFixed(1)}MB (max 20MB).`);
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/api/case-locker/cases/${caseId}/attachments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!data.ok) {
        setUploadError(data.detail || 'Upload failed.');
      }
    } catch (err) {
      setUploadError('Upload failed. Please try again.');
    }
    setUploading(false);
    loadCase();
  }

  function onFileInputChange(e) {
    uploadFilesList(e.target.files);
    e.target.value = ''; // allow re-selecting same file
  }

  function onDragOver(e) { e.preventDefault(); e.stopPropagation(); setDragActive(true); }
  function onDragLeave(e) { e.preventDefault(); e.stopPropagation(); setDragActive(false); }
  function onDrop(e) {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    uploadFilesList(e.dataTransfer.files);
  }

  async function exportPdf() {
    const res = await fetch(`${API}/api/case-locker/cases/${caseId}/export-pdf`, { headers: headers(false) });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `case_${caseId}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function downloadAttachment(att) {
    const url = `${API}/api/case-locker/cases/${caseId}/attachments/${att.id}?download=1`;
    const res = await fetch(url, { headers: headers(false) });
    if (!res.ok) return;
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = att.filename;
    a.click();
    URL.revokeObjectURL(objUrl);
  }

  function inlineAttachmentUrl(att) {
    return `${API}/api/case-locker/cases/${caseId}/attachments/${att.id}`;
  }

  if (loading) return <div className="cl-loading">Loading case...</div>;
  if (!c) return <div className="cl-empty">Case not found</div>;

  const timeline = [...(c.timeline || [])].reverse();

  return (
    <div className="cl-detail" data-testid="case-detail">
      {/* Header */}
      <div className="cl-detail-header">
        <div className="cl-detail-header-left">
          <span className="cl-detail-id">{c.case_id}</span>
          {editMode ? (
            <input
              className="cl-input cl-edit-title"
              data-testid="edit-case-title-input"
              value={editForm?.title ?? ''}
              onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Case title"
            />
          ) : (
            <h1 className="cl-detail-title" data-testid="case-detail-title">{c.title}</h1>
          )}
          <div className="cl-detail-meta">
            {editMode ? (
              <>
                <select
                  className="cl-select cl-edit-inline"
                  data-testid="edit-case-issue-select"
                  value={editForm?.issue_type || 'other'}
                  onChange={(e) => setEditForm(f => ({ ...f, issue_type: e.target.value }))}
                >
                  {ISSUE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input
                  type="date"
                  className="cl-input cl-edit-inline"
                  data-testid="edit-case-date-input"
                  value={editForm?.incident_date || ''}
                  onChange={(e) => setEditForm(f => ({ ...f, incident_date: e.target.value }))}
                />
              </>
            ) : (
              <>
                <IssueLabel type={c.issue_type} />
                {c.incident_date && <span>Incident: {c.incident_date}</span>}
                <span>Created: {c.created_at?.slice(0, 10)}</span>
              </>
            )}
          </div>
        </div>
        <div className="cl-detail-header-right">
          {editMode ? (
            <>
              <button
                type="button"
                className="cl-btn-sm"
                data-testid="cancel-edit-btn"
                onClick={cancelEdit}
                disabled={editSaving}
              >
                <X size={14} /> Cancel
              </button>
              <button
                type="button"
                className="cl-btn-primary"
                data-testid="save-edit-btn"
                onClick={saveEdit}
                disabled={editSaving || !editForm?.title.trim()}
              >
                <Save size={14} /> {editSaving ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : (
            <>
              <div className="cl-status-dropdown">
                <button className="cl-status-trigger" data-testid="status-dropdown" onClick={() => setShowStatusMenu(!showStatusMenu)}>
                  <StatusBadge status={c.status} /> <ChevronDown size={14} />
                </button>
                {showStatusMenu && (
                  <div className="cl-status-menu">
                    {STATUS_OPTIONS.map(s => (
                      <button key={s.value} className="cl-status-option" onClick={() => changeStatus(s.value)}>
                        <span className="cl-status-dot" style={{ background: s.color }} />
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button className="cl-btn-sm" data-testid="edit-case-btn" onClick={enterEditMode}>
                <Edit3 size={14} /> Edit
              </button>
              <button className="cl-btn-sm" data-testid="export-pdf-btn" onClick={exportPdf}>
                <Download size={14} /> PDF
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary (editable) */}
      <div className="cl-section" data-testid="case-summary-section">
        <h3 className="cl-section-title"><FileText size={14} /> Summary</h3>
        {editMode ? (
          <textarea
            className="cl-textarea"
            data-testid="edit-case-summary-input"
            rows={4}
            value={editForm?.summary || ''}
            onChange={(e) => setEditForm(f => ({ ...f, summary: e.target.value }))}
            placeholder="Describe what happened…"
          />
        ) : (
          c.summary
            ? <p className="cl-section-body">{c.summary}</p>
            : <p className="cl-empty-text">No summary yet.</p>
        )}
      </div>

      {/* People + Witnesses */}
      <div className="cl-section-row">
        <div className="cl-section cl-section-half" data-testid="people-section">
          <h3 className="cl-section-title">
            <Users size={14} /> People Involved
            ({editMode ? (editForm?.people_involved?.length || 0) : (c.people_involved?.length || 0)})
          </h3>
          {editMode ? (
            <>
              <div className="cl-inline-add">
                <input className="cl-input" placeholder="Name"
                  value={editPerson.name}
                  onChange={(e) => setEditPerson(p => ({ ...p, name: e.target.value }))}
                  data-testid="edit-person-name"
                />
                <input className="cl-input" placeholder="Role"
                  value={editPerson.role}
                  onChange={(e) => setEditPerson(p => ({ ...p, role: e.target.value }))}
                />
                <button type="button" className="cl-btn-sm" onClick={addEditPerson} data-testid="edit-person-add">
                  <Plus size={14} />
                </button>
              </div>
              <div>
                {editForm?.people_involved?.map((p, i) => (
                  <span key={i} className="cl-tag">
                    {p.name}{p.role ? ` (${p.role})` : ''}
                    <button type="button"
                      onClick={() => setEditForm(f => ({
                        ...f,
                        people_involved: f.people_involved.filter((_, j) => j !== i),
                      }))}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </>
          ) : (
            c.people_involved?.length > 0
              ? c.people_involved.map((p, i) => (
                <div key={i} className="cl-person">
                  <span className="cl-person-name">{p.name || p}</span>
                  {p.role && <span className="cl-person-role">{p.role}</span>}
                  {p.notes && <span className="cl-person-notes">{p.notes}</span>}
                </div>
              ))
              : <p className="cl-empty-text">None listed.</p>
          )}
        </div>
        <div className="cl-section cl-section-half" data-testid="witnesses-section">
          <h3 className="cl-section-title">
            <Eye size={14} /> Witnesses
            ({editMode ? (editForm?.witnesses?.length || 0) : (c.witnesses?.length || 0)})
          </h3>
          {editMode ? (
            <>
              <div className="cl-inline-add">
                <input className="cl-input" placeholder="Witness name"
                  value={editWitness}
                  onChange={(e) => setEditWitness(e.target.value)}
                  data-testid="edit-witness-name"
                />
                <button type="button" className="cl-btn-sm" onClick={addEditWitness} data-testid="edit-witness-add">
                  <Plus size={14} />
                </button>
              </div>
              <div>
                {editForm?.witnesses?.map((w, i) => (
                  <span key={i} className="cl-tag">{w}
                    <button type="button"
                      onClick={() => setEditForm(f => ({
                        ...f,
                        witnesses: f.witnesses.filter((_, j) => j !== i),
                      }))}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </>
          ) : (
            c.witnesses?.length > 0
              ? c.witnesses.map((w, i) => <div key={i} className="cl-witness">{w}</div>)
              : <p className="cl-empty-text">None listed.</p>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="cl-section">
        <h3 className="cl-section-title"><Edit3 size={14} /> Notes</h3>
        {editMode ? (
          <textarea
            className="cl-textarea"
            data-testid="edit-case-notes-input"
            rows={3}
            value={editForm?.notes || ''}
            onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Additional notes or context…"
          />
        ) : (
          c.notes
            ? <p className="cl-section-body">{c.notes}</p>
            : <p className="cl-empty-text">No notes yet.</p>
        )}
      </div>

      {/* Attachments with drag-drop + preview */}
      <div className="cl-section" data-testid="attachments-section">
        <h3 className="cl-section-title"><Paperclip size={14} /> Attachments ({c.attachments?.length || 0})</h3>

        {c.attachments?.length > 0 && (
          <div className="cl-attachment-grid" data-testid="attachment-grid">
            {c.attachments.map(a => (
              <AttachmentCard
                key={a.id}
                att={a}
                inlineUrl={inlineAttachmentUrl(a)}
                onDownload={() => downloadAttachment(a)}
                getAuthHeaders={() => headers(false)}
              />
            ))}
          </div>
        )}

        <div
          className={`cl-dropzone ${dragActive ? 'cl-dropzone-active' : ''}`}
          data-testid="case-dropzone"
          onDragOver={onDragOver}
          onDragEnter={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          <Upload size={16} />
          <div>
            <div className="cl-dropzone-title">
              {uploading ? 'Uploading…' : (dragActive ? 'Drop file here' : 'Drag & drop a file or click to browse')}
            </div>
            <div className="cl-dropzone-hint">JPG, PNG, or PDF · max 20MB</div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={onFileInputChange}
            accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
            data-testid="case-file-input"
          />
        </div>
        {uploadError && (
          <div className="cl-upload-error" data-testid="upload-error">
            <AlertTriangle size={12} /> {uploadError}
          </div>
        )}
      </div>

      {/* Linked Outputs */}
      <div className="cl-section" data-testid="linked-outputs-section">
        <h3 className="cl-section-title"><Link2 size={14} /> Linked Outputs</h3>
        {c.linked_details?.fact_finders?.map(ff => (
          <div key={ff.case_id} className="cl-linked-item">
            <Shield size={14} />
            <span>Fact Finder: <b>{ff.title || ff.case_id}</b></span>
          </div>
        ))}
        {c.linked_details?.statements?.map(st => (
          <div key={st.statement_id} className="cl-linked-item">
            <MessageSquare size={14} />
            <span>Statement: <b>{st.statement_id}</b> ({st.created_at?.slice(0, 10)})</span>
          </div>
        ))}
        {c.linked_details?.grievances?.map(g => (
          <div key={g.draft_id} className="cl-linked-item">
            <AlertTriangle size={14} />
            <span>Grievance: <b>{g.grievance_title || g.draft_id}</b></span>
          </div>
        ))}
        {(!c.linked_details?.fact_finders?.length && !c.linked_details?.statements?.length && !c.linked_details?.grievances?.length) && (
          <p className="cl-empty-text">No linked outputs yet. Use Fact Finder, Statement Builder, or Grievance Builder and link their output to this case.</p>
        )}
      </div>

      {/* P78: Grievance Documentation Pack — §§8-10 */}
      <GrievantFactSheetPanel caseId={caseId} />
      <StewardFactSheetPanel caseId={caseId} />
      <SpecTimelinePanel caseId={caseId} />

      {/* P76: Step 2 Grievance Book Checklist */}
      <Step2ChecklistPanel caseId={caseId} />

      {/* Timeline */}
      <div className="cl-section" data-testid="timeline-section">
        <h3 className="cl-section-title"><Clock size={14} /> Timeline ({c.timeline?.length || 0})</h3>
        <div className="cl-add-note">
          <input className="cl-input" data-testid="note-input" value={noteText} onChange={e => setNoteText(e.target.value)}
            placeholder="Add a note to this case..." onKeyDown={e => e.key === 'Enter' && addNote()} />
          <button className="cl-btn-sm" data-testid="add-note-btn" onClick={addNote} disabled={!noteText.trim()}>Add Note</button>
        </div>
        <div className="cl-timeline">
          {timeline.map((t, i) => (
            <div key={i} className="cl-tl-entry" data-testid={`timeline-entry-${i}`}>
              <div className="cl-tl-dot" />
              <div className="cl-tl-content">
                <div className="cl-tl-header">
                  <span className="cl-tl-action">{t.action_type?.replace(/_/g, ' ')}</span>
                  <span className="cl-tl-time">{t.timestamp?.slice(0, 19).replace('T', ' ')}</span>
                </div>
                <p className="cl-tl-desc">{t.description}</p>
                {t.actor && <span className="cl-tl-actor">by {t.actor}</span>}
                {t.source_type && <span className="cl-tl-source">[{t.source_type}: {t.source_id}]</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// ATTACHMENT CARD (image thumbnail or PDF icon)
// ──────────────────────────────────────────────
function AttachmentCard({ att, inlineUrl, onDownload, getAuthHeaders }) {
  const [thumbUrl, setThumbUrl] = useState(null);
  const isImage = (att.content_type || '').startsWith('image/');
  const isPdf = (att.content_type || '') === 'application/pdf';

  // Fetch image thumbnail with auth headers via blob URL
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    if (!isImage) return;
    (async () => {
      try {
        const res = await fetch(inlineUrl, { headers: getAuthHeaders() });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        revoke = objUrl;
        if (!cancelled) setThumbUrl(objUrl);
      } catch { /* noop */ }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [inlineUrl, isImage, getAuthHeaders]);

  async function openInline() {
    // Fetch with auth, open as blob URL in new tab
    try {
      const res = await fetch(inlineUrl, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, '_blank', 'noopener,noreferrer');
      // Let the browser hold the URL until tab closes; skip early revoke.
    } catch { /* noop */ }
  }

  return (
    <div className="cl-att-card" data-testid={`attachment-card-${att.id}`}>
      <button
        type="button"
        className="cl-att-thumb"
        onClick={openInline}
        data-testid={`attachment-open-${att.id}`}
        title={`Open ${att.filename}`}
      >
        {isImage && thumbUrl ? (
          <img src={thumbUrl} alt={att.filename} />
        ) : isPdf ? (
          <FileText size={28} />
        ) : (
          <ImageIcon size={28} />
        )}
      </button>
      <div className="cl-att-info">
        <div className="cl-att-name" title={att.filename}>{att.filename}</div>
        <div className="cl-att-meta">
          <span>{isPdf ? 'PDF' : (isImage ? 'Image' : att.content_type)}</span>
          <button
            type="button"
            className="cl-att-download"
            onClick={onDownload}
            data-testid={`attachment-download-${att.id}`}
          >
            <Download size={12} /> Download
          </button>
        </div>
      </div>
    </div>
  );
}


export { CaseDetail, AttachmentCard };
